import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';

const UNITS = ['None', 'pcs', 'g', 'kg', 'ml', 'L', 'pack'];

const AISLES = ['Vegetables', 'Snacks', 'Bakery', 'Frozen', 'Canned', 'Drinks'];

const AISLE_COLORS: Record<string, string> = {
    Vegetables: '#5C9A5C',
    Snacks: '#C2703F',
    Bakery: '#C99A53',
    Frozen: '#6E93B0',
    Canned: '#9C8AA6',
    Drinks: '#4E9AA0',
};

export type AddEntry = {
    name: string;
    amount?: number;
    unit?: string;
    aisle?: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onAdd: (entry: AddEntry) => void;
    recentNames: string[];
};

export function AddItemComposer({ open, onClose, onAdd, recentNames }: Props) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';

    // ── Derived colors ─────────────────────────────────────────────────────────
    const bg = theme.palette.background.default;
    const cardBg = theme.palette.background.paper;
    const cardBorder = isLight ? '#EDE0CC' : '#3D2A15';
    const textPrimary = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;
    const mutedText = isLight ? '#9A9182' : '#7A6A52';
    const primary = theme.palette.primary.main;
    const primaryDark = theme.palette.primary.dark;
    const chipBg = isLight ? '#F5EAD8' : '#2E1E0A';
    const chipBgSelected = primary;
    const stepperBg = isLight ? '#F2EBDC' : '#2A1A08';

    const [name, setName] = useState('');
    const [amount, setAmount] = useState(1);
    const [unit, setUnit] = useState<string | null>(null);
    const [aisle, setAisle] = useState<string | null>(null);
    const [amountEditing, setAmountEditing] = useState(false);
    const [amountInput, setAmountInput] = useState('1');
    const nameRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => nameRef.current?.focus(), 80);
            return () => clearTimeout(timer);
        }
    }, [open]);

    const reset = () => {
        setName(''); setAmount(1); setAmountInput('1');
        setUnit(null); setAisle(null); setAmountEditing(false);
    };

    const submit = () => {
        if (!name.trim()) return;
        const entry: AddEntry = { name: name.trim() };
        if (unit && unit !== 'None') { entry.amount = amount; entry.unit = unit; }
        if (aisle) entry.aisle = aisle;
        onAdd(entry);
        reset();
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') submit();
        if (e.key === 'Escape') { reset(); onClose(); }
    };

    const commitAmountInput = () => {
        const val = parseFloat(amountInput);
        if (!isNaN(val) && val > 0) setAmount(val);
        else setAmountInput(String(amount));
        setAmountEditing(false);
    };

    const decrement = () => { const next = Math.max(0.5, amount - 1); setAmount(next); setAmountInput(String(next)); };
    const increment = () => { const next = amount + 1; setAmount(next); setAmountInput(String(next)); };

    if (!open) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1300,
            display: 'flex', flexDirection: 'column',
            background: bg, fontFamily: "'Inter', sans-serif",
            overflowY: 'auto',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '18px 22px 10px', flexShrink: 0,
            }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: textPrimary, letterSpacing: '-.02em' }}>
                    {t('shoppingList')}
                </div>
                <button
                    onClick={() => { reset(); onClose(); }}
                    style={{
                        background: 'none', border: 'none',
                        fontSize: 13, fontWeight: 500, color: mutedText,
                        cursor: 'pointer', padding: 0,
                    }}
                >
                    {t('cancel')}
                </button>
            </div>

            {/* Composer card */}
            <div style={{ padding: '2px 16px 16px', flexShrink: 0 }}>
                <div style={{
                    background: cardBg,
                    border: `1.5px solid ${primary}`,
                    borderRadius: 18, padding: '14px 15px 15px',
                    boxShadow: `0 12px 26px -10px ${primary}40`,
                }}>
                    {/* Name row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                            background: primary, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 19, lineHeight: 1,
                        }}>+</div>
                        <input
                            ref={nameRef}
                            value={name}
                            onChange={e => setName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('sl_itemName')}
                            style={{
                                flex: 1, fontSize: 18, fontWeight: 600,
                                color: textPrimary, background: 'transparent',
                                border: 'none', outline: 'none', minWidth: 0,
                                fontFamily: "'Inter', sans-serif",
                            }}
                        />
                        <button
                            onClick={submit}
                            style={{
                                flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
                                height: 36, padding: '0 14px', borderRadius: 11,
                                background: `linear-gradient(135deg, ${primary}, ${primaryDark})`,
                                color: '#fff', fontSize: 13, fontWeight: 600,
                                border: 'none', cursor: 'pointer',
                                boxShadow: `0 4px 10px -3px ${primary}80`,
                                fontFamily: "'Inter', sans-serif",
                            }}
                        >
                            {t('sl_add')} <span style={{ fontSize: 15 }}>↵</span>
                        </button>
                    </div>

                    <div style={{ margin: '9px 0 2px 38px', fontSize: 12, color: mutedText }}>
                        {t('sl_addHelper')}
                    </div>

                    {/* Amount section */}
                    <div style={{ marginTop: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 8 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: mutedText }}>{t('amount')}</span>
                            <span style={{ fontSize: 10, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '.08em', color: mutedText, opacity: 0.7 }}>{t('sl_optional')}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                            {/* Stepper */}
                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', background: stepperBg, borderRadius: 11, padding: 3 }}>
                                <button
                                    onClick={decrement}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8, background: cardBg,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 18, fontWeight: 500, color: mutedText,
                                        border: 'none', cursor: 'pointer',
                                    }}
                                >–</button>
                                {amountEditing ? (
                                    <input
                                        type="number" value={amountInput} min={0} step="any" autoFocus
                                        onChange={e => setAmountInput(e.target.value)}
                                        onBlur={commitAmountInput}
                                        onKeyDown={e => { if (e.key === 'Enter') commitAmountInput(); }}
                                        style={{
                                            width: 48, textAlign: 'center', fontSize: 16, fontWeight: 600,
                                            color: textPrimary, border: `1px solid ${primary}`,
                                            borderRadius: 4, background: cardBg, padding: '2px 4px',
                                            fontFamily: "'Inter', sans-serif",
                                        }}
                                    />
                                ) : (
                                    <span
                                        onClick={() => { setAmountInput(String(amount)); setAmountEditing(true); }}
                                        style={{
                                            minWidth: 30, textAlign: 'center', fontSize: 16, fontWeight: 600,
                                            color: textPrimary, cursor: 'text', padding: '0 4px',
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >{Number.isInteger(amount) ? amount : amount.toFixed(1)}</span>
                                )}
                                <button
                                    onClick={increment}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8, background: cardBg,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 18, fontWeight: 500, color: primary,
                                        border: 'none', cursor: 'pointer',
                                    }}
                                >+</button>
                            </div>

                            {/* Unit chips */}
                            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {UNITS.map(u => {
                                    const selected = u === 'None' ? unit === null : unit === u;
                                    const label = u === 'None' ? t('sl_unitNone')
                                        : u === 'pcs' ? t('sl_unitPcs')
                                        : u === 'pack' ? t('sl_unitPack')
                                        : u;
                                    return (
                                        <button
                                            key={u}
                                            onClick={() => setUnit(u === 'None' ? null : u)}
                                            style={{
                                                fontSize: 12, fontWeight: 600,
                                                color: selected ? '#fff' : textSecondary,
                                                background: selected ? `linear-gradient(135deg, ${primary}, ${primaryDark})` : chipBg,
                                                padding: '6px 11px', borderRadius: 9,
                                                border: 'none', cursor: 'pointer',
                                                fontFamily: "'Inter', sans-serif",
                                            }}
                                        >{label}</button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Aisle section */}
                    <div style={{ marginTop: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 8 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: mutedText }}>{t('sl_aisle')}</span>
                            <span style={{ fontSize: 10, fontWeight: 400, textTransform: 'uppercase', letterSpacing: '.08em', color: mutedText, opacity: 0.7 }}>{t('sl_optional')}</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                            <button
                                onClick={() => setAisle(null)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    fontSize: 12, fontWeight: 600,
                                    color: aisle === null ? '#fff' : textSecondary,
                                    background: aisle === null ? '#A99E8B' : chipBg,
                                    border: 'none',
                                    padding: '6px 11px', borderRadius: 20, cursor: 'pointer',
                                    fontFamily: "'Inter', sans-serif",
                                }}
                            >{t('sl_noAisle')}</button>

                            {AISLES.map(a => (
                                <button
                                    key={a}
                                    onClick={() => setAisle(a)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        fontSize: 12, fontWeight: 600,
                                        color: aisle === a ? '#fff' : textSecondary,
                                        background: aisle === a ? AISLE_COLORS[a] : chipBg,
                                        border: 'none',
                                        padding: '6px 11px', borderRadius: 20, cursor: 'pointer',
                                        fontFamily: "'Inter', sans-serif",
                                    }}
                                >
                                    <span style={{
                                        width: 7, height: 7, borderRadius: '50%',
                                        background: aisle === a ? 'rgba(255,255,255,.6)' : AISLE_COLORS[a],
                                        display: 'inline-block', flexShrink: 0,
                                    }} />
                                    {t('sl_' + a.toLowerCase(), a)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Recent items */}
                    {recentNames.length > 0 && (
                        <div style={{ marginTop: 14 }}>
                            <div style={{
                                fontSize: 10, fontWeight: 700, letterSpacing: '.14em',
                                textTransform: 'uppercase', color: mutedText, marginBottom: 8,
                            }}>
                                {t('sl_recent')}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                                {recentNames.slice(0, 8).map(n => (
                                    <button
                                        key={n}
                                        onClick={() => setName(n)}
                                        style={{
                                            fontSize: 12, fontWeight: 500,
                                            color: name === n ? primaryDark : textSecondary,
                                            background: name === n
                                                ? (isLight ? 'rgba(217,119,6,0.12)' : 'rgba(245,158,11,0.15)')
                                                : chipBg,
                                            padding: '6px 11px', borderRadius: 20,
                                            border: 'none', cursor: 'pointer',
                                            fontFamily: "'Inter', sans-serif",
                                        }}
                                    >{n}</button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
